<script setup lang="ts">
import type { PostPropertyDisplayAttributes } from '@@/vuecomponents/SurfacePostProperties.vue'
import { processPostBody } from '@@/vuecomposables/process_post_or_comment_body'
import { onMounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    darkMode: boolean
    postProperty: PostPropertyDisplayAttributes
    propertyValueClass?: string
    as?: string
  }>(),
  {
    propertyValueClass: 'text-body-small',
    as: 'dd',
  },
)

function assertIsString(value: unknown): asserts value is string {
  if (typeof value !== 'string') {
    throw new Error('Expected string but got: ' + typeof value)
  }
}

const formatUrl = (string: string) => {
  if (string.startsWith('http://') || string.startsWith('https://')) {
    return string
  }
  return `https://${string}`
}

const urlFieldValue = ref<string | null>(null)
const urlFieldValueRef = ref<HTMLElement | null>(null)

function removeProtocol(url: string) {
  return url.replace(/^(https?:\/\/)/, '')
}

const displayUrl = (string: string) => {
  try {
    const { origin, pathname } = new URL(string)
    if (pathname.slice(1).length > 30) {
      return removeProtocol(origin) + pathname.slice(0, 23) + '...'
    }
    return removeProtocol(string)
  } catch (e) {
    return removeProtocol(string)
  }
}

const replaceTextContent = (elementString: string) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(elementString, 'text/html')
  const aTag = doc.querySelector('a')
  if (!aTag) return null
  assertIsString(props.postProperty.formattedValue)
  aTag.textContent = displayUrl(formatUrl(props.postProperty.formattedValue))
  return aTag.outerHTML
}

const processUrlFieldValue = async (value: string): Promise<void> => {
  if (!urlFieldValueRef.value) return
  const string = await processPostBody(value, urlFieldValueRef.value)
  const newString = replaceTextContent(string)
  if (!newString) return
  urlFieldValue.value = newString
}

onMounted(async () => {
  assertIsString(props.postProperty.formattedValue)
  await processUrlFieldValue(formatUrl(props.postProperty.formattedValue))
})

watch(
  () => props.postProperty.formattedValue,
  async (newValue) => {
    assertIsString(newValue)
    await processUrlFieldValue(formatUrl(newValue))
  },
)
</script>

<template>
  <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component -->
  <component
    :is="as"
    ref="urlFieldValueRef"
    :class="[
      'url-field-value',
      propertyValueClass,
      darkMode && 'text-light-text-100',
      !darkMode && 'text-dark-text-100',
    ]"
    data-testid="surfacePostPropertiesUrlFieldValue"
    v-html="urlFieldValue"
  ></component>
</template>
<style>
.url-field-value a {
  @apply underline;
  @apply text-current;
  text-underline-position: from-font;
  text-decoration-thickness: from-font;
}
</style>
